import {
  CheckOutlined,
  CloseOutlined,
  ControlOutlined,
} from '@ant-design/icons'
import FormCard from '@components/FormCard'
import { useHasAccess } from '@components/HasAccess'
import Page from '@components/Page/Page'
import { Venue } from '@components/venue/Venue'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { SettingKey } from '@models/Setting'
import { DominoConfig as DominoConfigModel } from '@models/venue/integrations/DominoConfig'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getSettings } from '@store/SettingReducer'
import {
  getDominoConfigs,
  selectDominoConfigs,
  setDominoConfigQuery,
} from '@store/venue/integrations/DominoConfigReducer'
import {
  EllipsisMiddle,
  PageTitle,
} from '@supplement-bacon/react-toolkit/components'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import { Permission } from '@utils/permissions'
import { requiredRule } from '@utils/rules'
import { Col, Row, Tooltip, type TableColumnsType } from 'antd'
import { useEffect } from 'react'

const IntegrationsDominoConfigsPage = () => {
  const dispatch = useAppDispatch()
  const { isOpen } = useSBRM()
  const dominoConfigs = useAppSelector(selectDominoConfigs())
  const { hasAccess } = useHasAccess()
  const { query, isLoading } = useAppSelector((state) => state.dominoConfig)

  const fetchSettings = () => {
    dispatch(
      getSettings([
        SettingKey.BUSINESS_TRIVEC_PAYMENT_METHOD,
        SettingKey.BUSINESS_TRIVEC_ACCOUNT_TYPE,
      ])
    )
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const columns: TableColumnsType<DominoConfigModel> = [
    {
      title: 'Venue',
      render: (_, record) => (
        <Venue.Avatar id={record.venue} size="small" displayExtraInfo />
      ),
    },
    {
      title: 'Host Api',
      dataIndex: 'hostApi',
      render: (text: string) => (
        <Tooltip title={text}>
          <EllipsisMiddle
            style={{ maxWidth: 250 }}
            suffixCount={20}
            copyable={{ text }}
          >
            {text}
          </EllipsisMiddle>
        </Tooltip>
      ),
    },

    {
      title: 'Host Export',
      dataIndex: 'hostExport',
      render: (text: string) => (
        <Tooltip title={text}>
          <EllipsisMiddle
            style={{ maxWidth: 250 }}
            suffixCount={20}
            copyable={{ text }}
          >
            {text}
          </EllipsisMiddle>
        </Tooltip>
      ),
    },
    {
      title: 'Is HQ',
      render: (record: DominoConfigModel) =>
        record.isHq ? (
          <CheckOutlined style={{ color: 'green' }} />
        ) : (
          <CloseOutlined style={{ color: '#ff4d4f' }} />
        ),
    },
    {
      align: 'right',
      render: (record: DominoConfigModel) => (
        <SBRM.Actions
          actions={[SBRMAction.Delete]}
          entity={Entity.dominoConfig}
          entityId={record.id}
          disabled={!hasAccess(Permission.manageBusiness)}
        />
      ),
    },
  ]

  const fetchDominoConfigs = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
    }

    dispatch(setDominoConfigQuery(newQuery))
    dispatch(getDominoConfigs(newQuery))
  }

  const { handleTableChange, params } = useTableUrlSync<DominoConfigModel>({
    onParamsChange: fetchDominoConfigs,
  })

  useEffect(() => {
    if (isOpen) return

    fetchDominoConfigs(params)
  }, [isOpen])

  return (
    <Page
      permissions={Permission.manageBusiness}
      pageTitle={{
        title: 'Trivec',
        level: 4,
      }}
    >
      <Row gutter={[32, 32]}>
        <Col span={12}>
          <FormCard
            title="Business trivec payment method"
            description={`This is the method used to close the table`}
            icon={<ControlOutlined />}
            entity={Entity.setting}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.Setting,
                value: SettingKey.BUSINESS_TRIVEC_PAYMENT_METHOD,
              },
              {
                key: MetadataKey.ExtraProps,
                value: {
                  label: 'Payment Method',
                  rules: [requiredRule()],
                },
              },
            ]}
          />
        </Col>
        <Col span={12}>
          <FormCard
            title="Business trivec account type"
            description="This is the customer account type used to identify Business clients. Note: this value is an integer."
            icon={<ControlOutlined />}
            entity={Entity.setting}
            action={SBRMAction.Update}
            metadata={[
              {
                key: MetadataKey.Setting,
                value: SettingKey.BUSINESS_TRIVEC_ACCOUNT_TYPE,
              },
              {
                key: MetadataKey.ExtraProps,
                value: {
                  label: 'Account Type',
                  rules: [requiredRule()],
                },
              },
            ]}
          />
        </Col>
      </Row>
      <PageTitle
        title="Trivec configurations"
        level={5}
        toolbar={
          <SBRM.Actions
            actions={[SBRMAction.Create]}
            entity={Entity.dominoConfig}
            disabled={!hasAccess(Permission.manageBusiness)}
          />
        }
      />

      <SBTable
        columns={columns}
        dataSource={dominoConfigs}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={query.pagination}
      />
    </Page>
  )
}

export default IntegrationsDominoConfigsPage
